// src/Payroll_Management/PayRoll_Report/SalaryHoldList_Print.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Printer, X } from "lucide-react";
import { ThemeContext } from "../../context/ThemeContext";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const SalaryHoldList_Print = () => {
  const { isDark } = useContext(ThemeContext);
  const q = useQuery();

  const finYearId = localStorage.getItem("finYearId") || "";

  const branchId = q.get("branchId") || "";
  const monthName = q.get("month") || "";
  const year = q.get("year") || "";
  const empName = q.get("empName") || "";
  const empCode = q.get("empCode") || "";

  const [company, setCompany] = useState({
    name: "",
    address1: "",
    address2: "",
    phone: "",
    email: "",
    logoUrl: "",
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // You can also hardcode a logo if DB logo is empty
  const fallbackLogo = "/logo.png"; // put in public/logo.png (or change)

  const fetchAll = async () => {
    if (!String(finYearId).trim()) return;

    setLoading(true);
    try {
      const [cRes, dRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/salary-hold-list/company-info`, {
          params: { finYearId: Number(finYearId) },
        }),
        axios.get(`${BASE_URL}/api/salary-hold-list/print-data`, {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            monthName: monthName,
            year: year,
            empName: empName,
            empCode: empCode,
          },
        }),
      ]);

      if (cRes?.data?.success) {
        setCompany(cRes.data.company || {});
      }

      if (dRes?.data?.success) {
        setRows(dRes.data.rows || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
      // Optional: auto print after load
      // setTimeout(() => window.print(), 600);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrint = () => window.print();
  const onClose = () => window.close();

  return (
    <div className={`${isDark ? "bg-gray-900 text-white" : "bg-gray-100 text-black"} min-h-screen p-4`}>
      {/* Print controls (hidden in print) */}
      <div className="no-print max-w-6xl mx-auto flex items-center justify-end gap-3 mb-3">
        <button
          onClick={onPrint}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow"
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-700 text-white font-bold hover:bg-gray-800 shadow"
        >
          <X className="w-4 h-4" />
          Close
        </button>
      </div>

      {/* Sheet */}
      <div className="sheet max-w-6xl mx-auto bg-white text-black p-4 border">
        {/* Header like your screenshot */}
        <div className="border border-black">
          <div className="grid grid-cols-[160px_1fr] gap-0 border-b border-black">
            <div className="p-2 border-r border-black flex items-center justify-center">
              <img
                src={company.logoUrl || fallbackLogo}
                alt="logo"
                style={{ width: 120, height: 80, objectFit: "contain" }}
                onError={(e) => {
                  e.currentTarget.src = fallbackLogo;
                }}
              />
            </div>

            <div className="p-2 text-center">
              <div className="text-xl font-extrabold">{company.name || "Company Name"}</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>

              <div className="text-xs mt-1 leading-4">
                {company.address1 ? <div>{company.address1}</div> : null}
                {company.address2 ? <div>{company.address2}</div> : null}
                {(company.phone || company.email) ? (
                  <div>
                    {company.phone ? `Phone: ${company.phone}` : ""}
                    {company.phone && company.email ? "   |   " : ""}
                    {company.email ? company.email : ""}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="text-center font-extrabold py-2 border-b border-black">
            SALARY HOLD LIST REPORT
          </div>

          {/* Filter line */}
          <div className="grid grid-cols-1 md:grid-cols-3 text-xs">
            <div className="p-2 border-r border-black">
              <b>Year:</b> {year}
            </div>
            <div className="p-2 border-r border-black">
              <b>Month:</b> {monthName}
            </div>
            <div className="p-2">
              <b>Branch ID:</b> {branchId}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-3 border border-black">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-black">
                <th className="border-r border-black p-2 text-xs text-center w-[70px]">S.No</th>
                <th className="border-r border-black p-2 text-xs text-center w-[160px]">Employee Code</th>
                <th className="p-2 text-xs text-center">Employee Name</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="p-6 text-center text-sm">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-6 text-center text-sm">
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx} className="border-t border-black">
                    <td className="border-r border-black p-2 text-xs text-center">{idx + 1}</td>
                    <td className="border-r border-black p-2 text-xs text-center">
                      {r.fld_employeecode}
                    </td>
                    <td className="p-2 text-xs text-center">{r.fld_employee}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-4 text-[10px] text-center">
          Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @page { size: A4 landscape; margin: 10mm; }
        .no-print { display: flex; }
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .sheet { border: none !important; }
        }
      `}</style>
    </div>
  );
};

export default SalaryHoldList_Print;
